'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import FoodModal from './FoodModal';
import { supabase, Category } from '@/lib/supabase';

const menuData = [
  // BURGERS
  {
    id: 101,
    name: 'Wagyu Smash Burger',
    description: 'Double wagyu patty, aged cheddar, truffle aioli, caramelized onions, brioche bun',
    price: 2450,
    rating: 4.9,
    reviews: 342,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10302578c-1772227279977.png',
    alt: 'Juicy double wagyu smash burger with melted cheese',
    badge: 'Best Seller',
    category: 'burgers',
    isVeg: false,
    prepTime: '18 min',
    calories: '780 kcal',
    variations: [
      { id: 'single', name: 'Single', priceAdd: 0 },
      { id: 'double', name: 'Double', priceAdd: 400 },
      { id: 'triple', name: 'Triple', priceAdd: 750 },
    ],
    addons: [
      { id: 'extra-cheese', name: 'Extra Cheese', price: 150 },
      { id: 'bacon', name: 'Crispy Bacon', price: 200 },
      { id: 'avocado', name: 'Avocado', price: 180 },
      { id: 'jalapenos', name: 'Jalapeños', price: 80 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot', 'Extra Hot'],
  },
  {
    id: 102,
    name: 'Classic Cheeseburger',
    description: 'Angus beef patty, American cheese, pickles, mustard, ketchup, sesame bun',
    price: 1350,
    rating: 4.7,
    reviews: 512,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bd019991-1772350968151.png',
    alt: 'Classic cheeseburger with fresh lettuce and tomato',
    category: 'burgers',
    isVeg: false,
    prepTime: '15 min',
    calories: '640 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'large', name: 'Large', priceAdd: 250 },
    ],
    addons: [
      { id: 'extra-patty', name: 'Extra Patty', price: 300 },
      { id: 'mushrooms', name: 'Sautéed Mushrooms', price: 120 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot'],
  },
  {
    id: 103,
    name: 'Crispy Chicken Burger',
    description: 'Southern fried chicken breast, coleslaw, pickled cucumbers, sriracha mayo',
    price: 1650,
    rating: 4.8,
    reviews: 289,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a5076b27-1772350969922.png',
    alt: 'Crispy fried chicken burger with coleslaw',
    badge: 'Popular',
    category: 'burgers',
    isVeg: false,
    prepTime: '20 min',
    calories: '720 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'spicy', name: 'Nashville Hot', priceAdd: 100 },
    ],
    addons: [
      { id: 'extra-sauce', name: 'Extra Sauce', price: 60 },
      { id: 'cheese', name: 'Cheese Slice', price: 100 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot', 'Extra Hot'],
  },
  // PIZZA
  {
    id: 201,
    name: 'Truffle Margherita',
    description: 'San Marzano tomatoes, buffalo mozzarella, black truffle oil, fresh basil, EVOO',
    price: 1890,
    rating: 4.8,
    reviews: 218,
    image: 'https://images.unsplash.com/photo-1599731316529-77e2c6b2734a',
    alt: 'Gourmet margherita pizza with truffle and fresh basil',
    badge: 'New',
    category: 'pizza',
    isVeg: true,
    prepTime: '22 min',
    calories: '620 kcal',
    variations: [
      { id: 'small', name: '8" Small', priceAdd: 0 },
      { id: 'medium', name: '12" Medium', priceAdd: 400 },
      { id: 'large', name: '16" Large', priceAdd: 800 },
    ],
    addons: [
      { id: 'extra-cheese', name: 'Extra Mozzarella', price: 200 },
      { id: 'olives', name: 'Kalamata Olives', price: 150 },
      { id: 'chili', name: 'Chili Flakes', price: 50 },
    ],
    spiceLevels: ['No Spice', 'Mild', 'Medium'],
  },
  {
    id: 202,
    name: 'BBQ Chicken Pizza',
    description: 'Smoky BBQ base, grilled chicken, red onion, corn, mozzarella, cilantro',
    price: 2100,
    rating: 4.7,
    reviews: 334,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1dcd5a06e-1772350971511.png',
    alt: 'BBQ chicken pizza with colorful toppings',
    category: 'pizza',
    isVeg: false,
    prepTime: '25 min',
    calories: '780 kcal',
    variations: [
      { id: 'small', name: '8" Small', priceAdd: 0 },
      { id: 'medium', name: '12" Medium', priceAdd: 400 },
      { id: 'large', name: '16" Large', priceAdd: 800 },
    ],
    addons: [
      { id: 'extra-chicken', name: 'Extra Chicken', price: 300 },
      { id: 'jalapenos', name: 'Jalapeños', price: 80 },
      { id: 'extra-cheese', name: 'Extra Cheese', price: 200 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot'],
  },
  {
    id: 203,
    name: 'Pepperoni Supreme',
    description:
      'Double pepperoni, Italian sausage, bell peppers, mushrooms, tomato sauce, mozzarella',
    price: 2250,
    rating: 4.9,
    reviews: 445,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_101f4e9e3-1772378186470.png',
    alt: 'Loaded pepperoni supreme pizza fresh from oven',
    badge: 'Best Seller',
    category: 'pizza',
    isVeg: false,
    prepTime: '24 min',
    calories: '850 kcal',
    variations: [
      { id: 'small', name: '8" Small', priceAdd: 0 },
      { id: 'medium', name: '12" Medium', priceAdd: 400 },
      { id: 'large', name: '16" Large', priceAdd: 800 },
    ],
    addons: [
      { id: 'extra-pepperoni', name: 'Extra Pepperoni', price: 250 },
      { id: 'stuffed-crust', name: 'Stuffed Crust', price: 350 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot'],
  },
  // PASTA
  {
    id: 301,
    name: 'Lobster Linguine',
    description: 'Fresh Atlantic lobster, cherry tomatoes, white wine, cream, garlic, fresh herbs',
    price: 4100,
    rating: 4.7,
    reviews: 97,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1218d001b-1772378180759.png',
    alt: 'Luxurious lobster linguine pasta in creamy white wine sauce',
    badge: 'Premium',
    category: 'pasta',
    isVeg: false,
    prepTime: '30 min',
    calories: '840 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'large', name: 'Large', priceAdd: 600 },
    ],
    addons: [
      { id: 'extra-lobster', name: 'Extra Lobster', price: 800 },
      { id: 'truffle', name: 'Truffle Shavings', price: 500 },
    ],
    spiceLevels: ['No Spice', 'Mild', 'Medium'],
  },
  {
    id: 302,
    name: 'Carbonara Classico',
    description: 'Guanciale, egg yolks, Pecorino Romano, black pepper, al dente spaghetti',
    price: 1750,
    rating: 4.8,
    reviews: 201,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_146ec8666-1772378183438.png',
    alt: 'Classic spaghetti carbonara with crispy guanciale',
    category: 'pasta',
    isVeg: false,
    prepTime: '20 min',
    calories: '760 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'large', name: 'Large', priceAdd: 350 },
    ],
    addons: [
      { id: 'extra-bacon', name: 'Extra Guanciale', price: 250 },
      { id: 'chili', name: 'Chili Oil', price: 80 },
    ],
    spiceLevels: ['No Spice', 'Mild'],
  },
  // SUSHI
  {
    id: 401,
    name: 'Dragon Roll Platter',
    description: 'Spicy tuna, avocado, cucumber, tobiko, ponzu glaze — 12 pieces',
    price: 3200,
    rating: 4.9,
    reviews: 189,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a5e47e1d-1772378185610.png',
    alt: 'Elegant dragon sushi roll platter with colorful toppings',
    badge: "Chef's Pick",
    category: 'sushi',
    isVeg: false,
    prepTime: '25 min',
    calories: '520 kcal',
    variations: [
      { id: '12pc', name: '12 Pieces', priceAdd: 0 },
      { id: '24pc', name: '24 Pieces', priceAdd: 2800 },
    ],
    addons: [
      { id: 'wasabi', name: 'Extra Wasabi', price: 50 },
      { id: 'ginger', name: 'Pickled Ginger', price: 50 },
      { id: 'miso', name: 'Miso Soup', price: 300 },
    ],
    spiceLevels: ['No Spice', 'Mild', 'Spicy'],
  },
  {
    id: 402,
    name: 'Salmon Sashimi',
    description:
      'Premium Norwegian salmon, thinly sliced, served with ponzu, wasabi, pickled ginger',
    price: 2800,
    rating: 4.8,
    reviews: 134,
    image: 'https://images.unsplash.com/photo-1637074930269-089fde202b57',
    alt: 'Fresh premium salmon sashimi slices on slate plate',
    category: 'sushi',
    isVeg: false,
    prepTime: '15 min',
    calories: '380 kcal',
    variations: [
      { id: '8pc', name: '8 Pieces', priceAdd: 0 },
      { id: '16pc', name: '16 Pieces', priceAdd: 2500 },
    ],
    addons: [
      { id: 'extra-wasabi', name: 'Extra Wasabi', price: 50 },
      { id: 'truffle-ponzu', name: 'Truffle Ponzu', price: 200 },
    ],
    spiceLevels: ['No Spice'],
  },
  // GRILLS
  {
    id: 501,
    name: 'Ribeye Steak 300g',
    description: '28-day dry-aged ribeye, herb butter, roasted garlic, truffle mashed potato',
    price: 5500,
    rating: 4.9,
    reviews: 156,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d6565efd-1772378184053.png',
    alt: 'Perfectly cooked ribeye steak with herb butter and sides',
    badge: 'Premium',
    category: 'grills',
    isVeg: false,
    prepTime: '35 min',
    calories: '1100 kcal',
    variations: [
      { id: 'rare', name: 'Rare', priceAdd: 0 },
      { id: 'medium-rare', name: 'Medium Rare', priceAdd: 0 },
      { id: 'medium', name: 'Medium', priceAdd: 0 },
      { id: 'well', name: 'Well Done', priceAdd: 0 },
    ],
    addons: [
      { id: 'foie-gras', name: 'Foie Gras', price: 800 },
      { id: 'truffle-sauce', name: 'Truffle Sauce', price: 400 },
      { id: 'extra-sides', name: 'Extra Sides', price: 350 },
    ],
    spiceLevels: ['No Spice', 'Mild Pepper'],
  },
  {
    id: 502,
    name: 'Mixed Grill Platter',
    description: 'Chicken tikka, seekh kebab, lamb chops, shami kebab, naan, raita, mint chutney',
    price: 3800,
    rating: 4.8,
    reviews: 278,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f83bf79a-1765635448910.png',
    alt: 'Sizzling mixed grill platter with various meats and sides',
    badge: 'Best Seller',
    category: 'grills',
    isVeg: false,
    prepTime: '40 min',
    calories: '1250 kcal',
    variations: [
      { id: 'half', name: 'Half (2 pax)', priceAdd: 0 },
      { id: 'full', name: 'Full (4 pax)', priceAdd: 2500 },
    ],
    addons: [
      { id: 'extra-naan', name: 'Extra Naan x2', price: 150 },
      { id: 'extra-raita', name: 'Extra Raita', price: 100 },
    ],
    spiceLevels: ['Mild', 'Medium', 'Hot', 'Extra Hot'],
  },
  // SALADS
  {
    id: 601,
    name: 'Caesar Salad',
    description: 'Romaine hearts, house-made Caesar dressing, Parmigiano-Reggiano, garlic croutons',
    price: 950,
    rating: 4.6,
    reviews: 167,
    image: 'https://images.unsplash.com/photo-1598148147935-05b3518efe32',
    alt: 'Fresh Caesar salad with croutons and parmesan',
    category: 'salads',
    isVeg: true,
    prepTime: '10 min',
    calories: '420 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'large', name: 'Large', priceAdd: 250 },
    ],
    addons: [
      { id: 'chicken', name: 'Grilled Chicken', price: 350 },
      { id: 'shrimp', name: 'Grilled Shrimp', price: 450 },
      { id: 'anchovies', name: 'Anchovies', price: 150 },
    ],
    spiceLevels: ['No Spice'],
  },
  // DESSERTS
  {
    id: 701,
    name: 'Lava Chocolate Cake',
    description: 'Valrhona dark chocolate, molten center, vanilla bean ice cream, raspberry coulis',
    price: 890,
    rating: 4.9,
    reviews: 423,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f4d14547-1771884523691.png',
    alt: 'Decadent chocolate lava cake with ice cream and raspberry',
    badge: "Chef's Pick",
    category: 'desserts',
    isVeg: true,
    prepTime: '15 min',
    calories: '680 kcal',
    variations: [
      { id: 'single', name: 'Single', priceAdd: 0 },
      { id: 'double', name: 'Double', priceAdd: 700 },
    ],
    addons: [
      { id: 'extra-ice-cream', name: 'Extra Ice Cream', price: 200 },
      { id: 'caramel', name: 'Caramel Drizzle', price: 100 },
      { id: 'berries', name: 'Mixed Berries', price: 180 },
    ],
    spiceLevels: [],
  },
  {
    id: 702,
    name: 'Mango Cheesecake',
    description: 'New York-style cheesecake, Alphonso mango compote, graham cracker crust',
    price: 750,
    rating: 4.7,
    reviews: 198,
    image: 'https://images.unsplash.com/photo-1596549347916-de3dca7b9ce6',
    alt: 'Creamy mango cheesecake slice with fruit topping',
    category: 'desserts',
    isVeg: true,
    prepTime: '5 min',
    calories: '520 kcal',
    variations: [
      { id: 'slice', name: 'Single Slice', priceAdd: 0 },
      { id: 'half', name: 'Half Cake', priceAdd: 1800 },
      { id: 'whole', name: 'Whole Cake', priceAdd: 3200 },
    ],
    addons: [
      { id: 'cream', name: 'Whipped Cream', price: 80 },
      { id: 'sauce', name: 'Extra Mango Sauce', price: 120 },
    ],
    spiceLevels: [],
  },
  // DRINKS
  {
    id: 801,
    name: 'Signature Lemonade',
    description: 'Fresh-squeezed lemons, mint, elderflower, sparkling water, crushed ice',
    price: 450,
    rating: 4.8,
    reviews: 312,
    image: 'https://images.unsplash.com/photo-1676844834412-26adad4dd100',
    alt: 'Refreshing mint lemonade in tall glass with ice',
    category: 'drinks',
    isVeg: true,
    prepTime: '5 min',
    calories: '180 kcal',
    variations: [
      { id: 'reg', name: 'Regular 350ml', priceAdd: 0 },
      { id: 'large', name: 'Large 500ml', priceAdd: 150 },
    ],
    addons: [
      { id: 'extra-mint', name: 'Extra Mint', price: 30 },
      { id: 'ginger', name: 'Ginger Shot', price: 80 },
    ],
    spiceLevels: [],
  },
  {
    id: 802,
    name: 'Cold Brew Coffee',
    description: '24-hour cold brew, oat milk, vanilla syrup, served over artisan ice',
    price: 550,
    rating: 4.7,
    reviews: 245,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13578d99b-1772378183506.png',
    alt: 'Cold brew coffee with oat milk in glass jar',
    category: 'drinks',
    isVeg: true,
    prepTime: '3 min',
    calories: '120 kcal',
    variations: [
      { id: 'reg', name: 'Regular', priceAdd: 0 },
      { id: 'large', name: 'Large', priceAdd: 120 },
    ],
    addons: [
      { id: 'extra-shot', name: 'Extra Shot', price: 100 },
      { id: 'caramel', name: 'Caramel Syrup', price: 80 },
    ],
    spiceLevels: [],
  },
];

interface CartItem {
  id: string;
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  image: string;
  variation?: string;
  addons: string[];
  spiceLevel?: string;
}

interface MenuGridProps {
  isDark: boolean;
  onAddToCart: (item: CartItem) => void;
  initialCategory?: string;
}

export default function MenuGrid({ isDark, onAddToCart, initialCategory = 'all' }: MenuGridProps) {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [selectedItem, setSelectedItem] = useState<(typeof menuData)[0] | null>(null);
  const [search, setSearch] = useState('');
  const [menuItems, setMenuItems] = useState<(typeof menuData)[0][]>(menuData);
  const [categories, setCategories] = useState<Category[]>([]);
  const categoryScrollRef = useRef<HTMLDivElement>(null);

  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const categoryIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((cat) => {
      map[cat.id.toString()] = cat.name.toLowerCase();
    });
    return map;
  }, [categories]);

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select(
          `
          *,
          categories (
            name
          )
        `
        )
        .eq('available', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const transformed = data.map((item: any) => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.price,
          rating: 4.5,
          reviews: Math.floor(Math.random() * 100) + 50,
          image: item.image_url || '/assets/images/no_image.png',
          alt: item.name,
          category: item.categories?.name?.toLowerCase() || 'all',
          isVeg: false,
          prepTime: '20 min',
          calories: '500 kcal',
          sort_order: item.sort_order || 0,
          variations:
            item.variations?.map((v: string, i: number) => ({
              id: `v${i}`,
              name: v,
              priceAdd: 0,
            })) || [],
          addons:
            item.addons?.map((a: string, i: number) => ({ id: `a${i}`, name: a, price: 100 })) ||
            [],
          spiceLevels: item.spice_levels || [],
        }));

        // Filter out items from the 6 removed categories
        const categoriesToRemove = ['burgers', 'pizza', 'sushi', 'grills', 'desserts', 'drinks'];
        const filteredItems = transformed.filter(
          (item) => !categoriesToRemove.includes(item.category)
        );
        setMenuItems(filteredItems);
      } else {
        // Fallback to hardcoded menu data if Supabase has no items
        const categoriesToRemove = ['burgers', 'pizza', 'sushi', 'grills', 'desserts', 'drinks'];
        const filteredMenuData = menuData.filter(
          (item) => !categoriesToRemove.includes(item.category)
        );
        setMenuItems(filteredMenuData);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      // Fallback to hardcoded menu data on error
      const categoriesToRemove = ['burgers', 'pizza', 'sushi', 'grills', 'desserts', 'drinks'];
      const filteredMenuData = menuData.filter(
        (item) => !categoriesToRemove.includes(item.category)
      );
      setMenuItems(filteredMenuData);
    }
  };

  const filtered = useMemo(() => {
    let result = menuItems.filter((item) => {
      const matchCat = activeCategory === 'all' || item.category === activeCategory;
      const matchSearch =
        search === '' ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });

    // Sort by category sort_order, then by item sort_order when ALL is selected
    if (activeCategory === 'all') {
      result = result.sort((a, b) => {
        const catA = categories.find((c) => c.name.toLowerCase() === a.category);
        const catB = categories.find((c) => c.name.toLowerCase() === b.category);
        const orderA = catA?.sort_order ?? 999;
        const orderB = catB?.sort_order ?? 999;

        // First sort by category sort_order
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // Then sort by item sort_order (if available), otherwise by id
        const itemOrderA = (a as any).sort_order ?? (a as any).id ?? 999;
        const itemOrderB = (b as any).sort_order ?? (b as any).id ?? 999;
        return itemOrderA - itemOrderB;
      });
    } else {
      // When specific category is selected, sort by item sort_order (if available), otherwise by id
      result = result.sort((a, b) => {
        const itemOrderA = (a as any).sort_order ?? (a as any).id ?? 999;
        const itemOrderB = (b as any).sort_order ?? (b as any).id ?? 999;
        return itemOrderA - itemOrderB;
      });
    }

    return result;
  }, [activeCategory, search, menuItems, categories]);

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#F5F5F0' : '#1A1A24';
  const textMuted = isDark ? 'rgba(245,245,240,0.5)' : '#6B6B7A';

  return (
    <>
      {/* Sticky Category Bar */}
      <div data-component-id="src/app/menu/components/MenuGrid.tsx:614:6" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="614" data-component-end-line="750" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22sticky%20top-16%20lg%3Atop-20%20z-30%20border-b%22%7D"
        className="sticky top-16 lg:top-20 z-30 border-b"
        style={{
          background: isDark ? 'rgba(10,10,15,0.95)' : 'rgba(250,248,243,0.95)',
          borderColor,
          backdropFilter: 'blur(20px)',
        }}
      >
        <div data-component-id="src/app/menu/components/MenuGrid.tsx:622:8" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="622" data-component-end-line="749" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar - Full Width Above Categories */}
          <div data-component-id="src/app/menu/components/MenuGrid.tsx:624:10" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="624" data-component-end-line="654" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-3%22%7D" className="py-3">
            <div data-component-id="src/app/menu/components/MenuGrid.tsx:625:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="625" data-component-end-line="653" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%22%7D" className="relative w-full">
              <input data-component-id="src/app/menu/components/MenuGrid.tsx:626:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="626" data-component-end-line="638" data-component-file="MenuGrid.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Asearch%5D%22%2C%22className%22%3A%22w-full%20pl-12%20pr-4%20py-3%20rounded-xl%20text-sm%20font-medium%20outline-none%20transition-all%20duration-300%22%7D"
                type="text"
                placeholder="Search dishes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl text-sm font-medium outline-none transition-all duration-300"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  border: `1px solid ${borderColor}`,
                  color: textPrimary,
                }}
                aria-label="Search menu items"
              />
              <svg data-component-id="src/app/menu/components/MenuGrid.tsx:639:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="639" data-component-end-line="652" data-component-file="MenuGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20w-5%20h-5%22%7D"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: textMuted }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path data-component-id="src/app/menu/components/MenuGrid.tsx:646:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="646" data-component-end-line="651" data-component-file="MenuGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Category Pills - Full Width with Scroll Buttons */}
          <div data-component-id="src/app/menu/components/MenuGrid.tsx:657:10" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="657" data-component-end-line="748" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22menu-section%22%2C%22className%22%3A%22relative%20flex%20items-center%20gap-2%22%7D" id="menu-section" className="relative flex items-center gap-2">
            <button data-component-id="src/app/menu/components/MenuGrid.tsx:658:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="658" data-component-end-line="675" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              onClick={() => scrollCategories('left')}
              className={`flex-shrink-0 p-2 rounded-xl transition-all duration-300 hover:scale-110 ${
                isDark
                  ? 'bg-white/10 text-white/70 hover:bg-white/20'
                  : 'bg-black/5 text-gray-600 hover:bg-black/10'
              }`}
              aria-label="Scroll left"
            >
              <svg data-component-id="src/app/menu/components/MenuGrid.tsx:667:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="667" data-component-end-line="674" data-component-file="MenuGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/app/menu/components/MenuGrid.tsx:668:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="668" data-component-end-line="673" data-component-file="MenuGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div data-component-id="src/app/menu/components/MenuGrid.tsx:676:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="676" data-component-end-line="729" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%20overflow-x-auto%20scroll-hide%20flex-1%20pb-3%22%7D"
              ref={categoryScrollRef}
              className="flex gap-2 overflow-x-auto scroll-hide flex-1 pb-3"
            >
              <button data-component-id="src/app/menu/components/MenuGrid.tsx:680:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="680" data-component-end-line="701" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key="all"
                onClick={() => setActiveCategory('all')}
                className={`category-pill flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-md font-bold transition-all duration-300 ${
                  activeCategory === 'all'
                    ? 'text-white shadow-lg'
                    : isDark
                      ? 'text-white/50 hover:text-white hover:bg-white/8'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'
                }`}
                style={
                  activeCategory === 'all'
                    ? {
                        background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                        boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
                      }
                    : {}
                }
              >
                <span data-component-id="src/app/menu/components/MenuGrid.tsx:699:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="699" data-component-end-line="699" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%F0%9F%8D%BD%EF%B8%8F%22%7D">🍽️</span>
                <span data-component-id="src/app/menu/components/MenuGrid.tsx:700:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="700" data-component-end-line="700" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22All%22%7D">All</span>
              </button>
              {categories.map((cat) => {
                const isActive = activeCategory === cat.name.toLowerCase();
                return (
                  <button data-component-id="src/app/menu/components/MenuGrid.tsx:705:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="705" data-component-end-line="726" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.name.toLowerCase())}
                    className={`category-pill flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-md font-bold transition-all duration-300 ${
                      isActive
                        ? 'text-white shadow-lg'
                        : isDark
                          ? 'text-white/50 hover:text-white hover:bg-white/8'
                          : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'
                    }`}
                    style={
                      isActive
                        ? {
                            background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                            boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
                          }
                        : {}
                    }
                  >
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:724:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="724" data-component-end-line="724" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{cat.icon}</span>
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:725:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="725" data-component-end-line="725" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{cat.name}</span>
                  </button>
                );
              })}
            </div>
            <button data-component-id="src/app/menu/components/MenuGrid.tsx:730:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="730" data-component-end-line="747" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              onClick={() => scrollCategories('right')}
              className={`flex-shrink-0 p-2 rounded-xl transition-all duration-300 hover:scale-110 ${
                isDark
                  ? 'bg-white/10 text-white/70 hover:bg-white/20'
                  : 'bg-black/5 text-gray-600 hover:bg-black/10'
              }`}
              aria-label="Scroll right"
            >
              <svg data-component-id="src/app/menu/components/MenuGrid.tsx:739:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="739" data-component-end-line="746" data-component-file="MenuGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/app/menu/components/MenuGrid.tsx:740:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="740" data-component-end-line="745" data-component-file="MenuGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div data-component-id="src/app/menu/components/MenuGrid.tsx:753:6" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="753" data-component-end-line="865" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-10%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {filtered.length === 0 ? (
          <div data-component-id="src/app/menu/components/MenuGrid.tsx:755:10" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="755" data-component-end-line="773" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20justify-center%20py-24%20gap-4%22%7D" className="flex flex-col items-center justify-center py-24 gap-4">
            <div data-component-id="src/app/menu/components/MenuGrid.tsx:756:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="756" data-component-end-line="756" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-6xl%22%2C%22textContent%22%3A%22%F0%9F%94%8D%22%7D" className="text-6xl">🔍</div>
            <p data-component-id="src/app/menu/components/MenuGrid.tsx:757:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="757" data-component-end-line="759" data-component-file="MenuGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-bold%20text-lg%22%2C%22textContent%22%3A%22No%20dishes%20found%22%7D" className="font-bold text-lg" style={{ color: textPrimary }}>
              No dishes found
            </p>
            <p data-component-id="src/app/menu/components/MenuGrid.tsx:760:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="760" data-component-end-line="762" data-component-file="MenuGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%22%2C%22textContent%22%3A%22Try%20a%20different%20search%20or%20category%22%7D" className="text-sm" style={{ color: textMuted }}>
              Try a different search or category
            </p>
            <button data-component-id="src/app/menu/components/MenuGrid.tsx:763:12" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="763" data-component-end-line="772" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20rounded-xl%20font-bold%20text-white%20text-sm%22%2C%22textContent%22%3A%22Clear%20Filters%22%7D"
              onClick={() => {
                setSearch('');
                setActiveCategory('all');
              }}
              className="px-6 py-3 rounded-xl font-bold text-white text-sm"
              style={{ background: 'linear-gradient(135deg, #F59E0B, #F97316)' }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div data-component-id="src/app/menu/components/MenuGrid.tsx:775:10" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="775" data-component-end-line="863" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%20gap-5%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((item) => (
              <div data-component-id="src/app/menu/components/MenuGrid.tsx:777:14" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="777" data-component-end-line="861" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                key={item.id}
                className={`food-card rounded-3xl overflow-hidden cursor-pointer ${isDark ? 'glass-card-dark' : 'glass-card-light'}`}
                onClick={() => setSelectedItem(item)}
              >
                {/* Image */}
                <div data-component-id="src/app/menu/components/MenuGrid.tsx:783:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="783" data-component-end-line="809" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-48%20overflow-hidden%22%7D" className="relative h-48 overflow-hidden">
                  <AppImage data-component-id="src/app/menu/components/MenuGrid.tsx:784:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="784" data-component-end-line="789" data-component-file="MenuGrid.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22object-cover%20food-card-img%22%7D"
                    src={item.image}
                    alt={item.alt}
                    fill
                    className="object-cover food-card-img"
                  />
                  <div data-component-id="src/app/menu/components/MenuGrid.tsx:790:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="790" data-component-end-line="790" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-t%20from-black%2F50%20via-transparent%20to-transparent%22%7D" className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div data-component-id="src/app/menu/components/MenuGrid.tsx:791:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="791" data-component-end-line="802" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-3%20left-3%20flex%20gap-1.5%22%7D" className="absolute top-3 left-3 flex gap-1.5">
                    {item.badge && (
                      <span data-component-id="src/app/menu/components/MenuGrid.tsx:793:22" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="793" data-component-end-line="795" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22badge-popular%20text-white%20px-2.5%20py-1%20rounded-lg%20text-xs%20font-bold%22%7D" className="badge-popular text-white px-2.5 py-1 rounded-lg text-xs font-bold">
                        {item.badge}
                      </span>
                    )}
                    {item.isVeg && (
                      <span data-component-id="src/app/menu/components/MenuGrid.tsx:798:22" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="798" data-component-end-line="800" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22badge-veg%20text-white%20px-2%20py-1%20rounded-lg%20text-xs%20font-bold%22%2C%22textContent%22%3A%22V%22%7D" className="badge-veg text-white px-2 py-1 rounded-lg text-xs font-bold">
                        V
                      </span>
                    )}
                  </div>
                  <div data-component-id="src/app/menu/components/MenuGrid.tsx:803:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="803" data-component-end-line="808" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-3%20right-3%20bg-black%2F60%20backdrop-blur-sm%20rounded-xl%20px-2.5%20py-1%20flex%20items-center%20gap-1%22%7D" className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm rounded-xl px-2.5 py-1 flex items-center gap-1">
                    <svg data-component-id="src/app/menu/components/MenuGrid.tsx:804:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="804" data-component-end-line="806" data-component-file="MenuGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3%20h-3%20star-filled%22%7D" className="w-3 h-3 star-filled" fill="currentColor" viewBox="0 0 20 20">
                      <path data-component-id="src/app/menu/components/MenuGrid.tsx:805:22" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="805" data-component-end-line="805" data-component-file="MenuGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:807:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="807" data-component-end-line="807" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%20text-xs%20font-bold%22%7D" className="text-white text-xs font-bold">{item.rating}</span>
                  </div>
                </div>

                {/* Content */}
                <div data-component-id="src/app/menu/components/MenuGrid.tsx:812:16" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="812" data-component-end-line="860" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%22%7D" className="p-4">
                  <h3 data-component-id="src/app/menu/components/MenuGrid.tsx:813:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="813" data-component-end-line="818" data-component-file="MenuGrid.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-display%20font-bold%20text-md%20leading-tight%20mb-1%22%7D"
                    className="font-display font-bold text-md leading-tight mb-1"
                    style={{ color: textPrimary }}
                  >
                    {item.name}
                  </h3>
                  <p data-component-id="src/app/menu/components/MenuGrid.tsx:819:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="819" data-component-end-line="824" data-component-file="MenuGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20leading-relaxed%20line-clamp-2%20mb-3%22%7D"
                    className="text-xs leading-relaxed line-clamp-2 mb-3"
                    style={{ color: textMuted }}
                  >
                    {item.description}
                  </p>
                  <div data-component-id="src/app/menu/components/MenuGrid.tsx:825:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="825" data-component-end-line="832" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20mb-3%20text-xs%22%7D"
                    className="flex items-center gap-3 mb-3 text-xs"
                    style={{ color: textMuted }}
                  >
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:829:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="829" data-component-end-line="829" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%E2%8F%B1%22%7D">⏱ {item.prepTime}</span>
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:830:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="830" data-component-end-line="830" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%F0%9F%94%A5%22%7D">🔥 {item.calories}</span>
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:831:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="831" data-component-end-line="831" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22(%20)%22%7D">({item.reviews})</span>
                  </div>
                  <div data-component-id="src/app/menu/components/MenuGrid.tsx:833:18" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="833" data-component-end-line="859" data-component-file="MenuGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                    <span data-component-id="src/app/menu/components/MenuGrid.tsx:834:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="834" data-component-end-line="836" data-component-file="MenuGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22price-tag%20font-black%20text-base%20text-amber-500%22%2C%22textContent%22%3A%22%E2%82%A8%22%7D" className="price-tag font-black text-base text-amber-500">
                      ₨{item.price.toLocaleString()}
                    </span>
                    <button data-component-id="src/app/menu/components/MenuGrid.tsx:837:20" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="837" data-component-end-line="858" data-component-file="MenuGrid.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%20text-xs%20font-bold%20px-4%20py-2%20rounded-xl%20transition-all%20duration-300%20bg-amber-500%2F15%20text-amber-500%20hover%3Abg-amber-500%20hover%3Atext-white%22%2C%22textContent%22%3A%22Add%22%7D"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(item);
                      }}
                      className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl transition-all duration-300 bg-amber-500/15 text-amber-500 hover:bg-amber-500 hover:text-white"
                    >
                      <svg data-component-id="src/app/menu/components/MenuGrid.tsx:844:22" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="844" data-component-end-line="856" data-component-file="MenuGrid.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3.5%20h-3.5%22%7D"
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path data-component-id="src/app/menu/components/MenuGrid.tsx:850:24" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="850" data-component-end-line="855" data-component-file="MenuGrid.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedItem && (
        <FoodModal data-component-id="src/app/menu/components/MenuGrid.tsx:869:8" data-component-path="src/app/menu/components/MenuGrid.tsx" data-component-line="869" data-component-end-line="877" data-component-file="MenuGrid.tsx" data-component-name="FoodModal" data-component-content="%7B%22elementName%22%3A%22FoodModal%22%7D"
          item={selectedItem}
          isDark={isDark}
          onClose={() => setSelectedItem(null)}
          onAddToCart={(cartItem) => {
            onAddToCart(cartItem);
            setSelectedItem(null);
          }}
        />
      )}
    </>
  );
}
